import Flex from "antd/es/flex";
import Typography from "antd/lib/typography";
import {Button, Card, ConfigProvider, DatePicker, Divider, Form, Image, Input, notification, Radio, Result, ThemeConfig} from "antd";
import {Fragment, useMemo, useState} from "react";
import axios from "axios";
import dayjs, {Dayjs} from "dayjs";
import {getBaseUrl} from "../../api/axiosInstance";
import "./styles/dispensary.less";

// Фирменный синий университета для кнопок и переключателей
const DISPENSARY_THEME: ThemeConfig = {
    token: {
        colorPrimary: "#2946a0",
        colorLink: "#2946a0",
    },
    components: {
        Button: {
            primaryShadow: "none",
        },
    },
};

interface IDispensaryForm {
    snils: string,
    lastName: string,
    firstName: string,
    middleName?: string,
    birthDate: Dayjs,
}

interface IQuestionOption {
    value: string,
    text: string,
}

type QuestionAnswerType = "boolean" | "string" | "select";

interface IQuestion {
    number: string,
    code: string,
    text: string,
    answerType: QuestionAnswerType,
    options?: IQuestionOption[],
}

export interface IQuestionnaireResponse {
    status: string,
    data: {
        questions: IQuestion[],
    },
}

// Персональные данные пациента — отправляются и при получении анкеты, и вместе с ответами
interface IPatient {
    snils: string,
    lastName: string,
    firstName: string,
    middleName: string,
    birthDate: string,
}

type AnswerValue = boolean | string | undefined;

const isAnswered = (question: IQuestion, value: AnswerValue) => {
    if (question.answerType === "boolean") return value === true || value === false;
    return typeof value === "string" && value.trim().length > 0;
};

// Ответ может прийти строкой (text/plain) — пробуем разобрать как JSON
const parsePayload = (data: unknown): any => {
    if (typeof data !== "string") return data;
    try {
        return JSON.parse(data);
    } catch {
        return data;
    }
};

interface IDispensaryError {
    code?: string,
    description: string,
}

const SERVER_UNAVAILABLE = "Сервер временно недоступен. Попробуйте позже.";
const CONTACT_MEDICAL_CENTER = "Обратитесь в медицинский центр.";

// Тексты для пользователя по кодам бизнес-ошибок МИС
const ERROR_DESCRIPTIONS: Record<string, string> = {
    // Ошибки формата запроса — ошибка на нашей стороне, подробности пользователю не нужны
    EMPTY_BODY: "Не удалось обработать запрос. Обновите страницу и попробуйте снова.",
    INVALID_JSON: "Не удалось обработать запрос. Обновите страницу и попробуйте снова.",
    VALIDATION_ERROR: "Не удалось обработать запрос. Обновите страницу и попробуйте снова.",
    PATIENT_NOT_FOUND: "Пациент по указанным данным не найден. Проверьте правильность СНИЛС, ФИО и даты рождения.",
    PATIENT_NOT_UNIQUE: "По указанным данным найдено несколько пациентов. " + CONTACT_MEDICAL_CENTER,
    MEDICAL_CARD_NOT_FOUND: "Не найдена ваша медицинская карта. " + CONTACT_MEDICAL_CENTER,
    MEDICAL_PROGRAM_NOT_FOUND: "Не найдена программа диспансеризации, доступная для прохождения. " + CONTACT_MEDICAL_CENTER,
    MEDICAL_DOCUMENT_NOT_FOUND: "Анкетирование в вашей программе диспансеризации ещё не назначено. " + CONTACT_MEDICAL_CENTER,
    MEDICAL_DOCUMENT_NOT_UNIQUE: "В вашей программе диспансеризации назначено несколько анкетирований. " + CONTACT_MEDICAL_CENTER,
    QUESTIONNAIRE_NOT_FOUND: "Анкета по диспансеризации не найдена. " + CONTACT_MEDICAL_CENTER,
    DOCUMENT_PROCESSING_ERROR: "Не удалось сохранить данные в медицинской системе. Попробуйте позже.",
    INTERNAL_ERROR: SERVER_UNAVAILABLE,
};

// Известный код — наш текст, неизвестный — сообщение МИС как есть
const describeBusinessError = (payload: any): IDispensaryError => {
    const code = typeof payload?.errorCode === "string" ? payload.errorCode : undefined;
    const message = typeof payload?.message === "string" && payload.message.trim() ? payload.message : undefined;
    return {code, description: (code && ERROR_DESCRIPTIONS[code]) || message || SERVER_UNAVAILABLE};
};

// Бизнес-ошибку МИС ({status: "error", errorCode, message}) разбираем по коду,
// прочие ответы сервера (HTML-страницы, стектрейсы) — общим текстом
const describeError = (error: any): IDispensaryError => {
    const errorPayload = parsePayload(error.response?.data);
    if (errorPayload?.status === "error") return describeBusinessError(errorPayload);
    if (error.response) return {description: SERVER_UNAVAILABLE};
    if (error.code === "ECONNABORTED") return {description: "Сервер не ответил вовремя. Попробуйте позже."};
    return {description: "Не удалось связаться с сервером. Проверьте подключение к интернету."};
};

const INVALID_RESPONSE: IDispensaryError = {description: "Сервер вернул некорректный ответ. Попробуйте позже."};

// Приводит ввод к виду "XXX-XXX-XXX XX"
const formatSnils = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    let result = digits.slice(0, 3);
    if (digits.length > 3) result += "-" + digits.slice(3, 6);
    if (digits.length > 6) result += "-" + digits.slice(6, 9);
    if (digits.length > 9) result += " " + digits.slice(9, 11);
    return result;
};

export default function DispensaryPage() {

    const [form] = Form.useForm<IDispensaryForm>();
    const [notificationApi, contextHolder] = notification.useNotification();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [alreadyCompleted, setAlreadyCompleted] = useState(false);
    const [patient, setPatient] = useState<IPatient | null>(null);
    const [questions, setQuestions] = useState<IQuestion[] | null>(null);
    const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});

    const answeredCount = useMemo(
        () => (questions ?? []).filter(q => isAnswered(q, answers[q.code])).length,
        [questions, answers]
    );
    const allAnswered = !!questions && questions.length > 0 && answeredCount === questions.length;

    const setAnswer = (code: string, value: AnswerValue) =>
        setAnswers(prev => ({...prev, [code]: value}));

    const handleError = (title: string, error: IDispensaryError) => {
        // Анкета уже пройдена — это не сбой, показываем отдельный экран вместо уведомления
        if (error.code === "QUESTIONNAIRE_ALREADY_EXISTS") {
            setQuestions(null);
            setAlreadyCompleted(true);
            return;
        }
        notificationApi.error({message: title, description: error.description});
    };

    const showError = (error: IDispensaryError) => handleError("Не удалось получить анкету", error);

    const showSubmitError = (error: IDispensaryError) => handleError("Не удалось отправить анкету", error);

    const onBackToForm = () => {
        setAlreadyCompleted(false);
        setQuestions(null);
        setPatient(null);
    };

    const onSubmitQuestionnaire = () => {
        if (!patient || !questions || !allAnswered) return;
        setSubmitting(true);
        const body = {
            ...patient,
            answers: questions.map(question => {
                const value = answers[question.code];
                // Для вариантов выбора отправляем текст ответа, а не его порядковый номер
                if (question.answerType === "select") {
                    const option = question.options?.find(o => o.value === value);
                    return {code: question.code, value: option?.text ?? value};
                }
                return {code: question.code, value: typeof value === "string" ? value.trim() : value};
            }),
        };
        axios.post(`${getBaseUrl()}/server/proxy/dispensary/questionnaire/submit`, body, {
            timeout: 30000,
        })
            .then(response => {
                const payload = parsePayload(response.data);
                if (payload?.status === "error") {
                    showSubmitError(describeBusinessError(payload));
                    return;
                }
                if (payload?.status !== "success") {
                    showSubmitError(INVALID_RESPONSE);
                    return;
                }
                notificationApi.success({
                    message: "Анкета успешно отправлена",
                    description: "Ваши ответы переданы в медицинский центр.",
                });
                setSubmitted(true);
            })
            .catch(error => showSubmitError(describeError(error)))
            .finally(() => setSubmitting(false));
    };

    const onFinish = (values: IDispensaryForm) => {
        setLoading(true);
        const body: IPatient = {
            snils: values.snils,
            lastName: values.lastName.trim(),
            firstName: values.firstName.trim(),
            middleName: values.middleName?.trim() ?? "",
            birthDate: values.birthDate.format("YYYY-MM-DD"),
        };
        // Обычный axios, а не axiosInstance: страница публичная, редирект на /login при 401 не нужен
        axios.post<IQuestionnaireResponse | string>(`${getBaseUrl()}/server/proxy/dispensary/questionnaire/init`, body, {
            timeout: 30000,
        })
            .then(response => {
                const payload = parsePayload(response.data);
                if (payload?.status === "error") {
                    showError(describeBusinessError(payload));
                    return;
                }
                if (payload?.status !== "success" || !Array.isArray(payload.data?.questions)) {
                    showError(INVALID_RESPONSE);
                    return;
                }
                setPatient(body);
                setAnswers({});
                setSubmitted(false);
                setAlreadyCompleted(false);
                setQuestions(payload.data.questions);
            })
            .catch(error => showError(describeError(error)))
            .finally(() => setLoading(false));
    };

    return (
        <ConfigProvider theme={DISPENSARY_THEME}>
            {contextHolder}
            <Flex vertical align="center" className="dispensary">
                <Flex vertical align="center" className={"dispensary-content" + (questions ? "" : " dispensary-content-form")}>
                    <Flex align="center" gap={16} className="dispensary-logo">
                        <Image preview={false} width={56} src={"/content/logo_lk.svg"}/>
                        <Typography.Text className="dispensary-logo-text">Российский университет транспорта</Typography.Text>
                    </Flex>
                    {submitted ? renderSubmitted()
                        : alreadyCompleted ? renderAlreadyCompleted()
                            : questions ? renderQuestionnaire() : renderForm()}
                </Flex>
            </Flex>
        </ConfigProvider>
    )

    function renderForm() {
        return <>
            <Typography.Paragraph className="dispensary-info">
                Для получения анкеты на диспансеризацию заполните форму
            </Typography.Paragraph>
            <Card className="dispensary-card">
                <Form form={form} layout="vertical" onFinish={onFinish} disabled={loading}>
                    <Form.Item label="СНИЛС" name="snils"
                               normalize={formatSnils}
                               rules={[
                                   {required: true, message: "Введите СНИЛС"},
                                   {pattern: /^\d{3}-\d{3}-\d{3} \d{2}$/, message: "Формат: 000-000-000 00"},
                               ]}>
                        <Input placeholder="000-000-000 00" inputMode="numeric"/>
                    </Form.Item>
                    <Form.Item label="Фамилия" name="lastName"
                               rules={[{required: true, whitespace: true, message: "Введите фамилию"}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Имя" name="firstName"
                               rules={[{required: true, whitespace: true, message: "Введите имя"}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Отчество" name="middleName">
                        <Input/>
                    </Form.Item>
                    <Form.Item label="Дата рождения" name="birthDate"
                               rules={[{required: true, message: "Укажите дату рождения"}]}>
                        <DatePicker format="DD.MM.YYYY" style={{width: "100%"}}
                                    disabledDate={date => date.isAfter(dayjs())}/>
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block>
                        Отправить
                    </Button>
                </Form>
            </Card>
        </>
    }

    function renderQuestionnaire() {
        return <>
            <Typography.Paragraph className="dispensary-info">
                Ответьте на все вопросы анкеты
            </Typography.Paragraph>
            <Card className="dispensary-questionnaire">
                <Flex vertical>
                    {questions!.map((question, index) =>
                        <Fragment key={question.code}>
                            {index > 0 && <Divider className="dispensary-question-divider"/>}
                            {renderQuestion(question)}
                        </Fragment>
                    )}
                </Flex>
                <Flex vertical gap={8} className="dispensary-questionnaire-footer">
                    <Typography.Text type="secondary">
                        Отвечено {answeredCount} из {questions!.length}
                    </Typography.Text>
                    <Button type="primary" block disabled={!allAnswered} loading={submitting}
                            onClick={onSubmitQuestionnaire}>
                        Отправить
                    </Button>
                </Flex>
            </Card>
        </>
    }

    function renderSubmitted() {
        return (
            <Card className="dispensary-card">
                <Result status="success" title="Анкета отправлена"
                        subTitle="Спасибо! Ваши ответы переданы в медицинский центр."/>
            </Card>
        )
    }

    function renderAlreadyCompleted() {
        return (
            <Card className="dispensary-card">
                <Result status="info" title="Анкета уже пройдена"
                        subTitle="Вы уже проходили анкетирование по диспансеризации в течение последних 12 месяцев. Повторно заполнять анкету не нужно."
                        extra={<Button onClick={onBackToForm}>Вернуться к форме</Button>}/>
            </Card>
        )
    }

    function renderQuestion(question: IQuestion) {
        const value = answers[question.code];
        const answered = isAnswered(question, value);
        // Подвопросы (1.1.1, 1.6.1) выводим с отступом
        const isSubQuestion = question.number.split(".").length > 2;
        return (
            <div className={"dispensary-question"
                     + (isSubQuestion ? " dispensary-question-sub" : "")
                     + (answered ? " dispensary-question-answered" : "")}>
                <Typography.Paragraph className="dispensary-question-text">
                    <span className="dispensary-question-number">{question.number}.</span> {question.text}
                </Typography.Paragraph>
                {question.answerType === "boolean" &&
                    <Radio.Group value={value} onChange={e => setAnswer(question.code, e.target.value)}
                                 optionType="button" buttonStyle="solid">
                        <Radio.Button value={true}>Да</Radio.Button>
                        <Radio.Button value={false}>Нет</Radio.Button>
                    </Radio.Group>
                }
                {question.answerType === "string" &&
                    <Input.TextArea value={value as string | undefined} autoSize={{minRows: 1, maxRows: 4}}
                                    onChange={e => setAnswer(question.code, e.target.value)}/>
                }
                {question.answerType === "select" &&
                    <Radio.Group value={value} onChange={e => setAnswer(question.code, e.target.value)}>
                        <Flex vertical gap={4}>
                            {(question.options ?? []).map(option =>
                                <Radio key={option.value} value={option.value}>{option.text}</Radio>
                            )}
                        </Flex>
                    </Radio.Group>
                }
            </div>
        )
    }
}
